import React from 'react';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram, ArrowUp } from 'lucide-react';
import { contactInfo } from '../data/mock';
import ArcheLogo from './ArcheLogo';

export default function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-black/50 border-t border-white/10 pt-20 pb-10">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Company Info */}
          <div>
            <div className="mb-6">
              <ArcheLogo variant="light" size="md" />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              From Concept to Creation — Engineering Tomorrow.
            </p>
            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#1E3A6F]/30 border border-white/10 hover:border-[#4A6FA5]/60 flex items-center justify-center transition-all duration-300">
                <Linkedin className="w-4 h-4 text-gray-400 hover:text-[#6B8FCC]" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#1E3A6F]/30 border border-white/10 hover:border-[#4A6FA5]/60 flex items-center justify-center transition-all duration-300">
                <Twitter className="w-4 h-4 text-gray-400 hover:text-[#6B8FCC]" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#1E3A6F]/30 border border-white/10 hover:border-[#4A6FA5]/60 flex items-center justify-center transition-all duration-300">
                <Facebook className="w-4 h-4 text-gray-400 hover:text-[#6B8FCC]" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#1E3A6F]/30 border border-white/10 hover:border-[#4A6FA5]/60 flex items-center justify-center transition-all duration-300">
                <Instagram className="w-4 h-4 text-gray-400 hover:text-[#6B8FCC]" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              <li><a href="#about" className="text-gray-400 hover:text-[#6B8FCC] text-sm transition-colors">About Us</a></li>
              <li><a href="#services" className="text-gray-400 hover:text-[#6B8FCC] text-sm transition-colors">Services</a></li>
              <li><a href="#industries" className="text-gray-400 hover:text-[#6B8FCC] text-sm transition-colors">Industries</a></li>
              <li><a href="#process" className="text-gray-400 hover:text-[#6B8FCC] text-sm transition-colors">Our Process</a></li>
              <li><a href="#contact" className="text-gray-400 hover:text-[#6B8FCC] text-sm transition-colors">Contact</a></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-bold mb-6">Services</h3>
            <ul className="space-y-3">
              <li><a href="#" className="text-gray-400 hover:text-[#6B8FCC] text-sm transition-colors">Industrial Design</a></li>
              <li><a href="#" className="text-gray-400 hover:text-[#6B8FCC] text-sm transition-colors">3D Printing</a></li>
              <li><a href="#" className="text-gray-400 hover:text-[#6B8FCC] text-sm transition-colors">CNC Machining</a></li>
              <li><a href="#" className="text-gray-400 hover:text-[#6B8FCC] text-sm transition-colors">Injection Molding</a></li>
              <li><a href="#" className="text-gray-400 hover:text-[#6B8FCC] text-sm transition-colors">Manufacturing</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-bold mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#6B8FCC] flex-shrink-0 mt-0.5" />
                <a
                  href={`mailto:${contactInfo.email}`}
                  data-testid="footer-email-link"
                  className="text-gray-400 hover:text-[#6B8FCC] text-sm transition-colors break-all"
                >
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#6B8FCC] flex-shrink-0 mt-0.5" />
                <a
                  href={`tel:${contactInfo.phoneRaw}`}
                  data-testid="footer-phone-link"
                  className="text-gray-400 hover:text-[#6B8FCC] text-sm transition-colors"
                >
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#6B8FCC] flex-shrink-0 mt-0.5" />
                <address className="text-gray-400 text-sm leading-relaxed not-italic" data-testid="footer-address">
                  {contactInfo.addressLines.map((line, idx) => (
                    <div key={idx}>{line}</div>
                  ))}
                </address>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} ARCHE Engineering Solutions. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-gray-500 hover:text-[#6B8FCC] text-sm transition-colors">Privacy Policy</a>
            <a href="#" className="text-gray-500 hover:text-[#6B8FCC] text-sm transition-colors">Terms of Service</a>
            <a href="#" className="text-gray-500 hover:text-[#6B8FCC] text-sm transition-colors">Sitemap</a>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        data-testid="back-to-top-btn"
        className="fixed bottom-8 right-8 w-12 h-12 rounded-full bg-gradient-to-br from-[#1E3A6F] to-[#4A6FA5] hover:from-[#2A4A85] hover:to-[#5B80B8] flex items-center justify-center shadow-2xl shadow-blue-900/50 hover:scale-110 transition-all duration-300 z-40"
        aria-label="Back to top"
      >
        <ArrowUp className="w-5 h-5 text-white" />
      </button>
    </footer>
  );
}
